import GlassdoorIcon from "components/icons/GlassdoorIcon";
import ReclameAquiIcon from "components/icons/ReclameAquiIcon";
import { format, lightFormat } from "date-fns";
import ptBR from "date-fns/locale/pt-BR";
import type { IBrazilianCompanyIssuerTopLevelFragment } from "lib/fragments/__generated__/BrazilianCompanyIssuerTopLevel.generated";
import { palette } from "lib/miolo";
import parseDateYmd from "lib/utils/parseDateYmd";

interface IBrazilianCompanyIssuerRatingsProps {
  issuer: IBrazilianCompanyIssuerTopLevelFragment;
}

function BrazilianCompanyIssuerRatings({ issuer }: IBrazilianCompanyIssuerRatingsProps) {
  return (
    <>
      {issuer.reclameAquiRatings && (
        <div className="space-y-4">
          <div className="space-y-4 rounded-xl p-2 py-0 font-aeonik">
            <div className="flex items-center space-x-2">
              <div className="flex h-12 w-12 items-center justify-center rounded-full bg-green-50 squircle">
                <ReclameAquiIcon className="h-7 w-7" />
              </div>

              <div>
                <h4 className="text-lg font-bold text-emerald-700">Reclame Aqui</h4>
                <a
                  className="text-sm font-medium italic text-gray-500 hover:underline"
                  href={issuer.reclameAquiUrl}
                  rel="noopener noreferrer"
                  target="_blank"
                >
                  Avaliação dos Consumidores
                </a>
              </div>
            </div>

            <div className="space-y-2 px-2">
              <div>
                <div className="space-x-2">
                  <span className="text-2xl font-bold text-gray-700">{issuer.reclameAquiRatings.finalScore} / 10</span>

                  <span
                    className="text-sm font-bold"
                    style={{
                      color: getReclameAquiClassificationTextColor(issuer.reclameAquiRatings.finalScore),
                    }}
                  >
                    {issuer.reclameAquiRatings.classification}
                  </span>
                </div>
              </div>

              <div>
                <RatingsAsOfDate asOfDate={issuer.reclameAquiRatings.asOfDate} />
              </div>
            </div>
          </div>
        </div>
      )}

      {issuer.glassdoorRatings && (
        <div className="space-y-4">
          <div className="space-y-4 rounded-xl p-2 py-0 font-aeonik">
            <div className="flex items-center space-x-2">
              <div className="flex h-12 w-12 items-center justify-center rounded-full bg-green-50 squircle">
                <GlassdoorIcon className="h-7 w-7" />
              </div>

              <div>
                <h4 className="text-lg font-bold text-emerald-700">Glassdoor</h4>
                <a
                  className="text-sm font-medium italic text-gray-500 hover:underline"
                  href={issuer.glassdoorUrl}
                  rel="noopener noreferrer"
                  target="_blank"
                >
                  Avaliação dos Funcionários
                </a>
              </div>
            </div>

            <div className="space-y-2 px-2">
              <div className="space-x-2">
                <span className="text-2xl font-bold text-gray-700">{issuer.glassdoorRatings.overallRating} / 5</span>
                <span className="text-sm font-normal text-gray-500">
                  <strong>{issuer.glassdoorRatings.allReviewsCount}</strong> avaliações
                </span>
              </div>

              <div>
                <span
                  className="text-sm font-medium tracking-wide"
                  style={{
                    color: issuer.glassdoorRatings.recommendToFriendRating > 0.5 ? palette.amazon : palette.mojo,
                  }}
                >
                  <strong>{Math.ceil(issuer.glassdoorRatings.recommendToFriendRating * 100)}%</strong> recomendam a um
                  amigo
                </span>
              </div>

              <div>
                <RatingsAsOfDate asOfDate={issuer.glassdoorRatings.asOfDate} />
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

interface IRatingsAsOfDateProps {
  asOfDate: string;
}

function RatingsAsOfDate({ asOfDate }: IRatingsAsOfDateProps) {
  const parsedAsOfDate = parseDateYmd(asOfDate);
  const timeText = format(parsedAsOfDate, "d 'de' MMMM, yyyy", {
    locale: ptBR,
  });

  return (
    <time
      className="text-xs font-normal italic tracking-wide text-slate-500"
      dateTime={parsedAsOfDate.toISOString()}
      title={lightFormat(parsedAsOfDate, "dd/MM/yyyy")}
    >
      Atualizado em <strong>{timeText}</strong>
    </time>
  );
}

export default BrazilianCompanyIssuerRatings;

function getReclameAquiClassificationTextColor(finalScore: number): string {
  // Ótimo
  if (finalScore >= 8 && finalScore <= 10) {
    return palette.goblin;
  }

  // Bom
  if (finalScore >= 7 && finalScore <= 7.9) {
    return palette.cobalt;
  }

  // Regular
  if (finalScore >= 6 && finalScore <= 6.9) {
    return palette.copper;
  }

  // Ruim
  if (finalScore >= 5 && finalScore <= 5.9) {
    return palette.mojo;
  }

  // Não Recomendada
  return palette.linkVisited;
}
